import { useState, useEffect } from 'react';
import { useAuthStore } from '../../lib/auth';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Clock, RefreshCw, Loader2, Shield, Download, Trash2 } from 'lucide-react';

export default function ScheduledBackup() {
  const [scheduledConfig, setScheduledConfig] = useState({
    enabled: false, collections: [], interval_type: 'daily', interval_value: 1,
    hour: 3, day_of_week: 0, compress: true, keep_last: 7,
    email_notification: false, email_addresses: []
  });
  const [allCollections, setAllCollections] = useState([]);
  const [backupHistory, setBackupHistory] = useState([]);
  const [scheduledLoading, setScheduledLoading] = useState(false);
  const [savingScheduled, setSavingScheduled] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  const fetchScheduledConfig = async () => {
    try {
      setScheduledLoading(true);
      const [configRes, historyRes, collectionsRes] = await Promise.all([
        api.get('/admin/scheduled-backup/config'),
        api.get('/admin/scheduled-backup/history'),
        api.get('/admin/database/collections')
      ]);
      if (configRes.data.success) setScheduledConfig(configRes.data.data);
      if (historyRes.data.success) setBackupHistory(historyRes.data.data || []);
      if (collectionsRes.data.success) setAllCollections(collectionsRes.data.data || []);
    } catch (error) {
      console.error('Error fetching scheduled config:', error);
    } finally {
      setScheduledLoading(false);
    }
  };

  const handleSaveScheduledConfig = async () => {
    try {
      setSavingScheduled(true);
      const response = await api.put('/admin/scheduled-backup/config', scheduledConfig);
      if (response.data.success) toast.success('Zamanlanmis yedekleme ayarlari kaydedildi');
    } catch (error) {
      toast.error('Ayarlar kaydedilemedi');
    } finally {
      setSavingScheduled(false);
    }
  };

  const handleRunBackupNow = async () => {
    try {
      const response = await api.post('/admin/scheduled-backup/run-now');
      if (response.data.success) { toast.success('Yedekleme baslatildi'); setTimeout(fetchScheduledConfig, 5000); }
    } catch (error) { toast.error('Yedekleme baslatilamadi'); }
  };

  const handleDownloadScheduledBackup = async (filename) => {
    try {
      const { accessToken } = useAuthStore.getState();
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${baseUrl}/api/admin/scheduled-backup/download/${filename}`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url; link.download = filename; link.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) { toast.error('Indirme hatasi'); }
  };

  const handleDeleteBackup = async (backupId) => {
    try {
      const response = await api.delete(`/admin/scheduled-backup/history/${backupId}`);
      if (response.data.success) { toast.success('Yedek silindi'); fetchScheduledConfig(); }
    } catch (error) { toast.error('Silme hatasi'); }
  };

  const handleAddEmail = () => {
    if (!newEmail || !newEmail.includes('@')) { toast.error('Gecerli bir email girin'); return; }
    if (scheduledConfig.email_addresses.includes(newEmail)) { toast.error('Bu email zaten ekli'); return; }
    setScheduledConfig(prev => ({ ...prev, email_addresses: [...prev.email_addresses, newEmail] }));
    setNewEmail('');
  };

  const handleRemoveEmail = (email) => {
    setScheduledConfig(prev => ({ ...prev, email_addresses: prev.email_addresses.filter(e => e !== email) }));
  };

  const toggleScheduledCollection = (collName) => {
    setScheduledConfig(prev => {
      const current = prev.collections || [];
      return current.includes(collName)
        ? { ...prev, collections: current.filter(c => c !== collName) }
        : { ...prev, collections: [...current, collName] };
    });
  };

  useEffect(() => { fetchScheduledConfig(); }, []);

  return (
    <Card data-testid="scheduled-backup-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Clock className="h-5 w-5 text-blue-500" />Zamanlanmis Yedekleme</CardTitle>
        <CardDescription>Belirli tablolari otomatik olarak yedekleyin</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {scheduledLoading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : (
          <>
            {/* Enable/Disable */}
            <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <div><h4 className="font-medium">Otomatik Yedekleme</h4><p className="text-sm text-slate-500">Zamanlanmis yedeklemeyi aktiflestir</p></div>
              <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" onClick={handleRunBackupNow} disabled={!scheduledConfig.enabled} data-testid="run-backup-now-btn">
                  <RefreshCw className="h-4 w-4 mr-1" />Simdi Calistir
                </Button>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={scheduledConfig.enabled} onChange={(e) => setScheduledConfig(prev => ({ ...prev, enabled: e.target.checked }))} className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>

            {/* Schedule Settings */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Yedekleme Sikligi</Label>
                <Select value={scheduledConfig.interval_type} onValueChange={(v) => setScheduledConfig(prev => ({ ...prev, interval_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hourly">Saatlik</SelectItem>
                    <SelectItem value="daily">Gunluk</SelectItem>
                    <SelectItem value="weekly">Haftalik</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {scheduledConfig.interval_type === 'hourly' && (
                <div className="space-y-2">
                  <Label>Kac Saatte Bir</Label>
                  <Select value={String(scheduledConfig.interval_value)} onValueChange={(v) => setScheduledConfig(prev => ({ ...prev, interval_value: parseInt(v) }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{[1,2,3,4,6,8,12].map(h => <SelectItem key={h} value={String(h)}>{h} saat</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {(scheduledConfig.interval_type === 'daily' || scheduledConfig.interval_type === 'weekly') && (
                <div className="space-y-2">
                  <Label>Saat</Label>
                  <Select value={String(scheduledConfig.hour)} onValueChange={(v) => setScheduledConfig(prev => ({ ...prev, hour: parseInt(v) }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Array.from({ length: 24 }, (_, i) => <SelectItem key={i} value={String(i)}>{String(i).padStart(2, '0')}:00</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {scheduledConfig.interval_type === 'weekly' && (
                <div className="space-y-2">
                  <Label>Gun</Label>
                  <Select value={String(scheduledConfig.day_of_week)} onValueChange={(v) => setScheduledConfig(prev => ({ ...prev, day_of_week: parseInt(v) }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Pazartesi</SelectItem><SelectItem value="1">Sali</SelectItem>
                      <SelectItem value="2">Carsamba</SelectItem><SelectItem value="3">Persembe</SelectItem>
                      <SelectItem value="4">Cuma</SelectItem><SelectItem value="5">Cumartesi</SelectItem>
                      <SelectItem value="6">Pazar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Additional Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Son Kac Yedegi Sakla</Label>
                <Select value={String(scheduledConfig.keep_last)} onValueChange={(v) => setScheduledConfig(prev => ({ ...prev, keep_last: parseInt(v) }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{[3,5,7,14,30].map(n => <SelectItem key={n} value={String(n)}>Son {n} yedek</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Format</Label>
                <div className="flex items-center gap-4 h-10">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={scheduledConfig.compress} onChange={(e) => setScheduledConfig(prev => ({ ...prev, compress: e.target.checked }))} className="w-4 h-4" />
                    <span className="text-sm">Sikistir (.gz)</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Collection Selection */}
            <div className="space-y-3">
              <Label>Yedeklenecek Tablolar</Label>
              <p className="text-sm text-slate-500">Bos birakilirsa tum tablolar yedeklenir</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg" data-testid="collection-checkboxes">
                {allCollections.map(coll => (
                  <label key={coll.name} className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 ${coll.important ? 'font-medium' : ''}`}>
                    <input type="checkbox" checked={scheduledConfig.collections.includes(coll.name)} onChange={() => toggleScheduledCollection(coll.name)} className="w-4 h-4" />
                    <span className="text-sm truncate">{coll.name}</span>
                    <span className="text-xs text-slate-400">({coll.count})</span>
                  </label>
                ))}
              </div>
              {scheduledConfig.collections.length > 0 && <p className="text-sm text-blue-600">{scheduledConfig.collections.length} tablo secili</p>}
            </div>

            {/* Email Notification */}
            <div className="space-y-3 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <div className="flex items-center justify-between">
                <div><h4 className="font-medium">Email Bildirimi</h4><p className="text-sm text-slate-500">Yedekleme tamamlandiginda bildirim gonder</p></div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={scheduledConfig.email_notification} onChange={(e) => setScheduledConfig(prev => ({ ...prev, email_notification: e.target.checked }))} className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                </label>
              </div>
              {scheduledConfig.email_notification && (
                <div className="space-y-2 pt-2">
                  <div className="flex gap-2">
                    <Input type="email" placeholder="email@example.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAddEmail()} />
                    <Button variant="outline" onClick={handleAddEmail}>Ekle</Button>
                  </div>
                  {scheduledConfig.email_addresses.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {scheduledConfig.email_addresses.map(email => (
                        <Badge key={email} variant="secondary" className="px-2 py-1">{email}<button onClick={() => handleRemoveEmail(email)} className="ml-2 hover:text-red-500">x</button></Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button onClick={handleSaveScheduledConfig} disabled={savingScheduled} data-testid="save-scheduled-config-btn">
                {savingScheduled ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Shield className="h-4 w-4 mr-2" />}Ayarlari Kaydet
              </Button>
            </div>

            {/* Backup History */}
            {backupHistory.length > 0 && (
              <div className="space-y-3 pt-4 border-t">
                <h4 className="font-medium">Yedekleme Gecmisi</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {backupHistory.map(backup => (
                    <div key={backup.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <div>
                        <p className="font-medium text-sm flex items-center gap-2">
                          {backup.filename}
                          {backup.s3_uploaded && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                              title="S3'e de yüklendi"
                              data-testid={`backup-s3-badge-${backup.id}`}
                            >
                              ☁ S3
                            </span>
                          )}
                          {backup.s3_error && !backup.s3_uploaded && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                              title={`S3 yüklemesi başarısız: ${backup.s3_error}`}
                            >
                              ☁ S3 hatası
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-slate-500">
                          {new Date(backup.created_at).toLocaleString('tr-TR')} | {(backup.file_size / 1024).toFixed(1)} KB |
                          <span className={backup.status === 'success' ? 'text-green-600' : 'text-red-600'}>{backup.status === 'success' ? ' Basarili' : ' Hata'}</span>
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {backup.status === 'success' && (
                          <Button size="sm" variant="outline" onClick={() => handleDownloadScheduledBackup(backup.filename)}><Download className="h-3 w-3" /></Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => handleDeleteBackup(backup.id)}><Trash2 className="h-3 w-3 text-red-500" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
